import { css } from 'styled-components';

import { Colour, Shadow } from '@xemplo/style-constants';

import { InputSize } from '../xemplo-input.types';

export const inputContainerStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  gap: 8px;
  border: 1px solid var(--input-border-color);
  width: var(--input-width);
  height: var(--input-height);
  background: var(--input-bg-color);
  border-radius: var(--input-border-radius);
`;

/** Input sizing styles. Should be applied to input container */
export const inputContainerSizeStyles = css`
  &.${InputSize.Compact} {
    --input-height: 40px;
    padding: 10px 16px;
  }

  &.${InputSize.Default} {
    --input-height: 48px;
    padding: 12px 16px;
  }

  &.${InputSize.Spacious} {
    --input-height: 64px;
    padding: 12px 16px;

    > input {
      height: unset;
    }

    &:has(input:not(:focus, .has-placeholder, .has-value), input.label-hidden) {
      padding: 20px 16px;
    }
  }
`;

export const inputFocus = css`
  --input-border-color: ${Colour.Blue[500]};
  --input-label-text-color: ${Colour.Blue[600]};
  border-radius: 8px;
  ${Shadow.Focus};
`;

export const inputContainerFocusWithin = css`
  &:focus-within:not(.invalid) {
    ${inputFocus};
  }
`;

/**
 * Input state styles for focus, disabled, readOnly, and invalid states.
 * Should be applied to the input container.
 */
export const inputContainerStateStyles = css`
  &.disabled,
  &.readOnly {
    pointer-events: none;
  }

  &.disabled {
    --input-bg-color: ${Colour.Gray[100]};
    --input-label-text-color: ${Colour.Gray[300]};
    --input-leading-icon-color: ${Colour.Gray[300]};
    --input-placeholder-text-color: ${Colour.Gray[300]};
    --input-trailing-icon-color: ${Colour.Gray[300]};
    --input-label-optional-text-color: ${Colour.Gray[300]};
  }

  &.readOnly {
    --input-bg-color: ${Colour.Gray[100]};
    --input-label-text-color: ${Colour.Gray[400]};
    --input-leading-icon-color: ${Colour.Gray[400]};
    --input-placeholder-text-color: ${Colour.Gray[400]};
    --input-text-color: ${Colour.Gray[500]};
    --input-trailing-icon-color: ${Colour.Gray[400]};
  }

  &.invalid {
    --input-border-color: ${Colour.Red[500]};
    --input-label-text-color: ${Colour.Red[600]};
    --input-trailing-icon-color: ${Colour.Red[500]};
    ${Shadow.Error2};
  }
`;

/**
 * Icon styling to be used within the input container.
 *
 * Per the Text Input spec, icons are 24px at Default and Spacious and 20px at
 * Compact. The Compact rule carries a class, so it outranks the base `> svg`
 * rule on specificity regardless of the order the two are composed in.
 */
export const inputContainerIconsStyles = css`
  > svg {
    flex: 0 0 auto;
    font-size: 24px;

    &:first-child {
      color: var(--input-leading-icon-color);
    }

    &:last-child:not(:first-child) {
      color: var(--input-trailing-icon-color);
    }

    &.error {
      color: var(--input-trailing-icon-color);
    }
  }

  &.${InputSize.Compact} > svg {
    font-size: 20px;
  }
`;

export const inputContainerBaseStyles = css`
  ${inputContainerStyles};
  ${inputContainerSizeStyles};
  ${inputContainerFocusWithin};
  ${inputContainerStateStyles};
  ${inputContainerIconsStyles};
`;
